import React, { useState } from "react";
import * as dmsService from "../../../services/dmsService";
import * as finService from "../../../services/finService";
import useFinOptions from "../../Fin/useFinOptions";
import useDmsOptions from "../useDmsOptions";
import { fmtMoney } from "../../Fin/gl/glContext";
import { DataTable, hint, linkBtn } from "../../Fin/gl/glUi";
import { ActionHistory, ChecklistForm, DmsBadge, DocHead, Panel, Section, run } from "../sales/dmsUi";
import { AppDropdown, EmptyState, Field, FormDrawer, LoadState, PageHeader, PrimaryButton, SecondaryButton, formatDate, formatDateTime, inputStyle, pageStyle, useLoad } from "../../../components/ui";

// Repair Orders: check a vehicle in (from its booking, or as a walk-in), work its jobs (clocking,
// parts, sublets), quality control, invoice (customer / warranty / internal) and the gate pass.

const PAY = [
  { label: "Customer", value: "CUSTOMER" },
  { label: "Warranty", value: "WARRANTY" },
  { label: "Internal", value: "INTERNAL" },
];
const STATUSES = ["OPEN", "IN_PROGRESS", "QC", "READY", "INVOICED", "GATE_PASSED", "CANCELLED"];
const label = (s) => String(s || "").charAt(0) + String(s || "").slice(1).toLowerCase().replace(/_/g, " ");
const blankJob = () => ({ complaint: "", pay_type: "CUSTOMER", labour_op_id: null, sold_hours: "" });

function CheckIn({ onHide, onDone }) {
  const fin = useFinOptions(["locations"]);
  const ops = useDmsOptions(["labour-operations"]);
  const [q, setQ] = useState("");
  const bookings = useLoad(() => dmsService.bookableBookings({ q: q || undefined }), [q]);
  const customers = useLoad(() => finService.listCustomers({ size: 300 }), []);
  const [mode, setMode] = useState("booking");
  const [h, setH] = useState({ group_id: null, location_id: null, customer_account_id: null, walkin_name: "", walkin_phone: "", vin: "", plate_no: "", vehicle_desc: "", odometer_in: "", fuel_level: "", promised_at: "", remarks: "" });
  const [jobs, setJobs] = useState([blankJob()]);
  const [busy, setBusy] = useState(false);
  const loc = h.location_id || (fin.locations && fin.locations[0] ? fin.locations[0].value : null);
  const setJob = (i, patch) => setJobs(jobs.map((j, k) => (k === i ? { ...j, ...patch } : j)));
  const save = async () => {
    setBusy(true);
    const body = {
      location_id: loc,
      odometer_in: h.odometer_in === "" ? undefined : Number(h.odometer_in),
      fuel_level: h.fuel_level || undefined,
      promised_at: h.promised_at || undefined,
      remarks: h.remarks || undefined,
      jobs: jobs.filter((j) => j.complaint.trim()).map((j) => ({ complaint: j.complaint, pay_type: j.pay_type, labour_op_id: j.labour_op_id || undefined, sold_hours: j.sold_hours === "" ? undefined : Number(j.sold_hours) })),
      ...(mode === "booking"
        ? { group_id: h.group_id }
        : {
            customer_account_id: h.customer_account_id || undefined,
            customer: h.customer_account_id ? undefined : { name: h.walkin_name, phone: h.walkin_phone },
            vin: h.vin || undefined,
            plate_no: h.plate_no || undefined,
            vehicle_desc: h.vehicle_desc || undefined,
          }),
    };
    const res = await run(() => dmsService.createRepairOrder(body));
    setBusy(false);
    if (res) {
      onDone(res.data.id);
      onHide();
    }
  };
  return (
    <Panel title="Check a vehicle in" onHide={() => !busy && onHide()} width={980} footer={<><SecondaryButton onClick={onHide} disabled={busy}>Cancel</SecondaryButton><PrimaryButton onClick={save} disabled={busy}>Open repair order</PrimaryButton></>}>
      <div className="btn-group mb-3" role="group" aria-label="Check-in from">
        <button type="button" className={`btn btn-sm ${mode === "booking" ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setMode("booking")}>
          From a booking
        </button>
        <button type="button" className={`btn btn-sm ${mode === "walkin" ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setMode("walkin")}>
          Walk-in
        </button>
      </div>
      {mode === "booking" ? (
        <>
          <input className="form-control mb-2" style={{ ...inputStyle, maxWidth: 320 }} placeholder="Booking number, customer or phone" aria-label="Search bookings" value={q} onChange={(e) => setQ(e.target.value)} />
          <LoadState loading={bookings.loading} error={bookings.error} onRetry={bookings.reload}>
            <DataTable
              rows={bookings.data || []}
              rowKey={(r) => r.booking_number}
              onRowClick={(r) => setH({ ...h, group_id: r.group_id })}
              empty={<EmptyState title="No bookings to check in" subtitle="Bookings from the last two weeks and the next week without a repair order show here." />}
              columns={[
                { label: "", render: (r) => <input type="radio" readOnly aria-label={`Choose ${r.booking_number}`} checked={h.group_id === r.group_id} /> },
                { label: "Booking", render: (r) => <b>{r.booking_number}</b> },
                { label: "Date", render: (r) => `${formatDate(r.service_date)} ${r.service_time || ""}` },
                { label: "Customer", render: (r) => r.customer_name || "" },
                { label: "Phone", render: (r) => r.phone || "" },
              ]}
            />
          </LoadState>
          <div style={hint}>The customer, the vehicle and the complaint come from the booking's job card.</div>
        </>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0 12px" }}>
          <Field label="Customer (blank: new walk-in)">
            <AppDropdown id="ro-cust" value={h.customer_account_id} filter options={[{ label: "New walk-in customer", value: null }, ...(customers.data || []).map((c) => ({ label: `${c.name} (${c.account_number})`, value: c.id }))]} onChange={(e) => setH({ ...h, customer_account_id: e.value })} />
          </Field>
          {!h.customer_account_id ? (
            <>
              <Field label="Name" required>
                <input className="form-control" style={inputStyle} value={h.walkin_name} onChange={(e) => setH({ ...h, walkin_name: e.target.value })} />
              </Field>
              <Field label="Phone" required>
                <input className="form-control" style={inputStyle} value={h.walkin_phone} onChange={(e) => setH({ ...h, walkin_phone: e.target.value })} />
              </Field>
            </>
          ) : null}
          <Field label="VIN">
            <input className="form-control" style={inputStyle} maxLength={17} value={h.vin} onChange={(e) => setH({ ...h, vin: e.target.value.toUpperCase() })} />
          </Field>
          <Field label="Plate">
            <input className="form-control" style={inputStyle} value={h.plate_no} onChange={(e) => setH({ ...h, plate_no: e.target.value })} />
          </Field>
          <Field label="Vehicle">
            <input className="form-control" style={inputStyle} placeholder="Make, model, year" value={h.vehicle_desc} onChange={(e) => setH({ ...h, vehicle_desc: e.target.value })} />
          </Field>
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "0 12px" }} className="mt-3">
        <Field label="Workshop">
          <AppDropdown id="ro-loc" value={loc} options={fin.locations || []} onChange={(e) => setH({ ...h, location_id: e.value })} />
        </Field>
        <Field label="Odometer (km)">
          <input type="number" min={0} className="form-control" style={inputStyle} value={h.odometer_in} onChange={(e) => setH({ ...h, odometer_in: e.target.value })} />
        </Field>
        <Field label="Fuel">
          <AppDropdown id="ro-fuel" value={h.fuel_level} options={["", "E", "1/4", "1/2", "3/4", "F"].map((f) => ({ label: f || "—", value: f }))} onChange={(e) => setH({ ...h, fuel_level: e.value })} />
        </Field>
        <Field label="Promised for">
          <input type="datetime-local" className="form-control" style={inputStyle} value={h.promised_at} onChange={(e) => setH({ ...h, promised_at: e.target.value })} />
        </Field>
      </div>
      <h6 className="mt-2">Jobs</h6>
      {jobs.map((j, i) => (
        <div key={i} className="d-flex flex-wrap gap-2 align-items-center mb-2">
          <input className="form-control" style={{ ...inputStyle, flex: 2, minWidth: 240 }} placeholder={mode === "booking" ? "Complaint (blank: use the booking's)" : "Complaint / work requested"} aria-label="Complaint" value={j.complaint} onChange={(e) => setJob(i, { complaint: e.target.value })} />
          <div style={{ width: 140 }}>
            <AppDropdown id={`ro-pay-${i}`} value={j.pay_type} options={PAY} onChange={(e) => setJob(i, { pay_type: e.value })} />
          </div>
          <div style={{ width: 220 }}>
            <AppDropdown id={`ro-op-${i}`} value={j.labour_op_id} filter options={[{ label: "No standard operation", value: null }, ...(ops["labour-operations"] || [])]} onChange={(e) => setJob(i, { labour_op_id: e.value })} />
          </div>
          <input type="number" min={0} step="any" className="form-control" style={{ ...inputStyle, width: 100 }} placeholder="Hours" aria-label="Hours" value={j.sold_hours} onChange={(e) => setJob(i, { sold_hours: e.target.value })} />
          {jobs.length > 1 ? (
            <button type="button" style={linkBtn} onClick={() => setJobs(jobs.filter((_, k) => k !== i))}>
              Remove
            </button>
          ) : null}
        </div>
      ))}
      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setJobs([...jobs, blankJob()])}>
        Add job
      </button>
    </Panel>
  );
}

function PartsDrawer({ ro, job, onHide, onDone }) {
  const parts = useLoad(() => dmsService.searchParts({ size: 100 }), []);
  const [lines, setLines] = useState([{ item_id: null, quantity: 1 }]);
  const [busy, setBusy] = useState(false);
  const set = (i, patch) => setLines(lines.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  const save = async () => {
    setBusy(true);
    const serials = (txt) => String(txt || "").split(/[\s,]+/).filter(Boolean).map((serial_no) => ({ serial_no }));
    const res = await run(() =>
      dmsService.repairOrderAction(ro.id, "parts", {
        job_id: job.id,
        lines: lines
          .filter((l) => l.item_id)
          .map((l) => ({ item_id: l.item_id, quantity: Number(l.quantity), details: l.serials ? serials(l.serials) : undefined, failed_serial_no: l.failed_serial_no || undefined, retained_bin: l.retained_bin || undefined })),
      })
    );
    setBusy(false);
    if (res) {
      onDone();
      onHide();
    }
  };
  return (
    <Panel title={`Parts for job ${job.job_no}`} onHide={() => !busy && onHide()} width={720} footer={<><SecondaryButton onClick={onHide} disabled={busy}>Cancel</SecondaryButton><PrimaryButton onClick={save} disabled={busy}>Post</PrimaryButton></>}>
      {lines.map((l, i) => (
        <div key={i} className="d-flex flex-wrap gap-2 align-items-center mb-2">
          <div style={{ flex: 1, minWidth: 260 }}>
            <AppDropdown id={`rp-it-${i}`} value={l.item_id} filter options={(parts.data || []).map((p) => ({ label: `${p.sku} – ${p.name}`, value: p.id }))} onChange={(e) => set(i, { item_id: e.value })} placeholder="Part" />
          </div>
          <input type="number" step="any" className="form-control" style={{ ...inputStyle, width: 110 }} aria-label="Quantity" value={l.quantity} onChange={(e) => set(i, { quantity: e.target.value })} />
          <input className="form-control" style={{ ...inputStyle, width: 180 }} placeholder="Serial no(s), if tracked" aria-label="Serial numbers" value={l.serials || ""} onChange={(e) => set(i, { serials: e.target.value })} />
          {job.pay_type === "WARRANTY" && Number(l.quantity) > 0 ? (
            <>
              <input className="form-control" style={{ ...inputStyle, width: 170 }} placeholder="Failed part's serial" aria-label="Failed part serial" value={l.failed_serial_no || ""} onChange={(e) => set(i, { failed_serial_no: e.target.value })} />
              <input className="form-control" style={{ ...inputStyle, width: 130 }} placeholder="Kept in bin" aria-label="Retained part bin" value={l.retained_bin || ""} onChange={(e) => set(i, { retained_bin: e.target.value })} />
            </>
          ) : null}
        </div>
      ))}
      <button type="button" className="btn btn-outline-secondary btn-sm mb-2" onClick={() => setLines([...lines, { item_id: null, quantity: 1 }])}>
        Add part
      </button>
      <div style={hint}>
        From warehouse {ro.warehouse_code}. A negative quantity returns a part to stores. Batches go earliest expiry first.
        {job.pay_type === "WARRANTY" ? " Warranty job: the failed part is tagged and kept for the manufacturer (Warranty Claims → Retained parts)." : ""}
      </div>
    </Panel>
  );
}

function JobCard({ ro, job, refresh, onParts, onSublet, onFinish, onEdit }) {
  const live = ["OPEN", "IN_PROGRESS"].includes(ro.status);
  const go = (action, data) => run(() => dmsService.jobAction(ro.id, job.id, action, data), refresh);
  return (
    <div style={{ borderTop: "1px solid #E5E7EB", padding: "12px 0" }}>
      <div className="d-flex flex-wrap justify-content-between gap-2">
        <div style={{ fontSize: 14 }}>
          <b>
            {job.job_no}. {job.complaint}
          </b>{" "}
          <DmsBadge status={job.status} /> {job.approval !== "APPROVED" ? <DmsBadge status={job.approval === "PENDING" ? "PENDING_APPROVAL" : "REJECTED"} /> : null}
          <div style={hint}>
            {label(job.pay_type)} · {job.op_code ? `${job.op_code} · ` : ""}
            {Number(job.sold_hours)} h × {fmtMoney(job.labour_rate)} = {fmtMoney(job.labour_amount)}
            {Number(job.labour_discount) ? ` less ${fmtMoney(job.labour_discount)}` : ""} · {job.technician_name || "no technician"} · clocked {Math.round(job.clocked_minutes / 6) / 10} h{job.clocked_on.length ? ` · on now: ${job.clocked_on.join(", ")}` : ""}
          </div>
          {job.cause ? <div style={{ fontSize: 13 }}>Cause: {job.cause}</div> : null}
          {job.correction ? <div style={{ fontSize: 13 }}>Correction: {job.correction}</div> : null}
        </div>
        <div className="d-flex flex-wrap gap-2 align-items-start">
          {job.approval === "PENDING" ? (
            <>
              <button type="button" style={linkBtn} onClick={() => go("decision", { decision: "APPROVED" })}>
                Customer approved
              </button>
              <button type="button" style={linkBtn} onClick={() => go("decision", { decision: "DECLINED" })}>
                Declined
              </button>
            </>
          ) : null}
          {live && job.approval === "APPROVED" && job.status !== "DONE" && job.status !== "CANCELLED" ? (
            job.clocked_on.length ? (
              <button type="button" style={linkBtn} onClick={() => go("clock-off")}>
                Clock off
              </button>
            ) : (
              <button type="button" style={linkBtn} onClick={() => go("clock-on")}>
                Clock on
              </button>
            )
          ) : null}
          {live && job.status !== "DONE" && job.status !== "CANCELLED" && job.approval === "APPROVED" ? (
            <button type="button" style={linkBtn} onClick={() => onFinish(job)}>
              Finish
            </button>
          ) : null}
          {["OPEN", "IN_PROGRESS", "QC"].includes(ro.status) && job.status !== "CANCELLED" ? (
            <>
              <button type="button" style={linkBtn} onClick={() => onParts(job)}>
                Parts
              </button>
              <button type="button" style={linkBtn} onClick={() => onSublet(job)}>
                Sublet
              </button>
              <button type="button" style={linkBtn} onClick={() => onEdit(job)}>
                Edit
              </button>
            </>
          ) : null}
          {live && job.status === "OPEN" && !job.clocked_minutes && !job.parts.length ? (
            <button type="button" style={linkBtn} onClick={() => window.confirm(`Cancel job ${job.job_no}?`) && go("decision", { decision: "CANCEL" })}>
              Cancel job
            </button>
          ) : null}
        </div>
      </div>
      {job.parts.length ? (
        <div style={{ fontSize: 13, marginTop: 4 }}>
          {job.parts.map((p, i) => (
            <div key={i}>
              {p.quantity < 0 ? "Returned" : "Issued"} {Math.abs(p.quantity)} × {p.sku} {p.item_name} @ {fmtMoney(p.unit_price)}
            </div>
          ))}
        </div>
      ) : null}
      {job.sublets.map((s, i) => (
        <div key={i} style={{ fontSize: 13 }}>
          Sublet: {s.description} by {s.supplier_name} · cost {fmtMoney(s.cost)} · price {fmtMoney(s.price)}
        </div>
      ))}
    </div>
  );
}

function RoView({ id, onHide, onChanged }) {
  const { data: ro, loading, error, reload } = useLoad(() => dmsService.getRepairOrder(id), [id]);
  const fin = useFinOptions(["suppliers"]);
  const ops = useDmsOptions(["labour-operations"]);
  const [partsFor, setPartsFor] = useState(null);
  const [subletFor, setSubletFor] = useState(null);
  const [finishing, setFinishing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [gate, setGate] = useState(false);
  const refresh = () => {
    reload();
    onChanged();
  };
  const doIt = (action, data) => run(() => dmsService.repairOrderAction(id, action, data), refresh);
  const st = ro ? ro.status : null;
  return (
    <Panel title={ro ? `Repair order ${ro.ro_number}` : "Repair order"} onHide={onHide} width={1040}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {ro ? (
          <>
            <DocHead
              title=""
              number={`${ro.customer_name}${ro.plate_no ? ` · ${ro.plate_no}` : ""}`}
              status={st}
              sub={`${ro.vehicle_desc || ""}${ro.vin ? ` · VIN ${ro.vin}` : ""} · ${ro.odometer_in ?? "?"} km · in ${formatDateTime(ro.checked_in_at)}${ro.promised_at ? ` · promised ${formatDateTime(ro.promised_at)}` : ""} · advisor ${ro.advisor_name || ""}`}
            >
              {["OPEN", "IN_PROGRESS"].includes(st) && !ro.customer_authorised ? <SecondaryButton onClick={() => doIt("authorise")}>Customer authorised</SecondaryButton> : null}
              {["OPEN", "IN_PROGRESS", "QC", "READY"].includes(st) ? <SecondaryButton onClick={() => setAdding(true)}>Add job</SecondaryButton> : null}
              {["OPEN", "IN_PROGRESS"].includes(st) ? <PrimaryButton onClick={() => doIt("qc")}>Send to QC</PrimaryButton> : null}
              {st === "READY" ? <PrimaryButton onClick={() => window.confirm("Invoice this repair order?") && doIt("invoice")}>Invoice</PrimaryButton> : null}
              {st === "INVOICED" ? <PrimaryButton onClick={() => setGate(true)}>Gate pass</PrimaryButton> : null}
              {["OPEN", "IN_PROGRESS"].includes(st) ? <SecondaryButton onClick={() => window.confirm("Cancel this repair order?") && doIt("cancel")}>Cancel</SecondaryButton> : null}
            </DocHead>
            {ro.recalls && ro.recalls.length ? (
              <div className="alert alert-warning py-2" role="alert">
                Open recall{ro.recalls.length > 1 ? "s" : ""}: {ro.recalls.map((r) => `${r.campaign_number} ${r.title}`).join("; ")}
              </div>
            ) : null}
            {ro.contracts && ro.contracts.length ? (
              <div className="alert alert-info py-2" role="status">
                Service contract: {ro.contracts.map((c) => `${c.contract_number} ${c.plan_name} (until ${formatDate(c.end_date)}${c.services_included ? `, ${c.services_used}/${c.services_included} used` : ""})`).join("; ")}
              </div>
            ) : null}
            <Section title="Jobs">
              {ro.jobs.map((j) => (
                <JobCard key={j.job_no} ro={ro} job={j} refresh={refresh} onParts={setPartsFor} onSublet={setSubletFor} onFinish={setFinishing} onEdit={setEditing} />
              ))}
            </Section>
            <Section title="Charges by who pays">
              <DataTable
                rows={["CUSTOMER", "WARRANTY", "INTERNAL"].map((p) => ({ pay: p, ...ro.totals[p] }))}
                rowKey={(r) => r.pay}
                columns={[
                  { label: "Pays", render: (r) => label(r.pay) },
                  { label: "Hours", num: true, render: (r) => r.hours },
                  { label: "Labour", num: true, render: (r) => fmtMoney(r.labour) },
                  { label: "Parts", num: true, render: (r) => fmtMoney(r.parts) },
                  { label: "Sublet", num: true, render: (r) => fmtMoney(r.sublet) },
                ]}
              />
              <div style={hint}>Before VAT. Estimate {ro.currency} {fmtMoney(ro.estimate_total)}{ro.customer_authorised ? " · authorised by the customer" : ""}.</div>
              {ro.invoices.length ? (
                <div className="mt-2" style={{ fontSize: 14 }}>
                  {ro.invoices.map((i) =>
                    i.ar_trx_id ? (
                      <div key={i.pay_type}>
                        Customer invoice {i.trx_number} ({fmtMoney(i.amount)}){" "}
                        <button type="button" style={linkBtn} onClick={() => finService.transactionPdf(i.ar_trx_id, `${i.trx_number}.pdf`)}>
                          Tax invoice
                        </button>
                      </div>
                    ) : (
                      <div key={i.pay_type}>Warranty claim {ro.claim ? `${ro.claim.claim_number} (${label(ro.claim.status)})` : ""}: {fmtMoney(i.amount)}</div>
                    )
                  )}
                </div>
              ) : null}
              {(ro.claims || []).filter((c) => c.claim_type !== "REPAIR").map((c) => (
                <div key={c.id} className="mt-1" style={{ fontSize: 14 }}>
                  {c.claim_type === "GOODWILL" ? "Goodwill" : "Supplementary"} warranty claim {c.claim_number} ({label(c.status)}): {fmtMoney(c.claimed_amount)}
                </div>
              ))}
            </Section>
            {["QC", "READY", "INVOICED", "GATE_PASSED"].includes(st) ? (
              <Section title="Quality control" right={st === "QC" ? <button type="button" style={linkBtn} onClick={() => doIt("qc/checklist", { failed: true })}>Back to the workshop</button> : null}>
                <ChecklistForm checklist={ro.qc} disabled={st !== "QC"} onSave={(body) => doIt("qc/checklist", body)} />
              </Section>
            ) : null}
            <Section title="Action history">
              <ActionHistory actions={ro.actions} />
            </Section>
          </>
        ) : null}
      </LoadState>
      {partsFor && ro ? <PartsDrawer ro={ro} job={partsFor} onHide={() => setPartsFor(null)} onDone={refresh} /> : null}
      <FormDrawer
        show={!!subletFor}
        title={subletFor ? `Sublet on job ${subletFor.job_no}` : ""}
        onHide={() => setSubletFor(null)}
        onDone={refresh}
        onSave={(clean) => dmsService.repairOrderAction(id, "sublets", { ...clean, job_id: subletFor.id, cost: Number(clean.cost), price: clean.price === "" || clean.price == null ? undefined : Number(clean.price) })}
        fields={[
          { key: "supplier_id", label: "Done by (supplier)", type: "select", required: true, options: fin.suppliers || [] },
          { key: "description", label: "Work", required: true, max: 240 },
          { key: "cost", label: "Cost", type: "number", step: "any", min: 0, required: true },
          { key: "price", label: "Price to the customer (blank: at cost)", type: "number", step: "any", min: 0 },
        ]}
      />
      <FormDrawer
        show={!!finishing}
        title={finishing ? `Finish job ${finishing.job_no}` : ""}
        initial={finishing ? { cause: finishing.cause || "", correction: finishing.correction || "" } : {}}
        onHide={() => setFinishing(null)}
        onDone={refresh}
        onSave={(clean) => dmsService.jobAction(id, finishing.id, "complete", clean)}
        fields={[
          { key: "cause", label: "Cause", type: "textarea", max: 1000 },
          { key: "correction", label: "Correction (what was done)", type: "textarea", required: true, max: 1000 },
        ]}
      />
      <FormDrawer
        show={!!editing}
        title={editing ? `Job ${editing.job_no}` : ""}
        initial={editing ? { complaint: editing.complaint, pay_type: editing.pay_type, sold_hours: Number(editing.sold_hours), labour_discount: Number(editing.labour_discount || 0), technician_employee_id: editing.technician_employee_id || null, cause: editing.cause || "", correction: editing.correction || "" } : {}}
        onHide={() => setEditing(null)}
        onDone={refresh}
        onSave={(clean) => dmsService.jobAction(id, editing.id, "", { ...clean, sold_hours: Number(clean.sold_hours || 0), labour_discount: Number(clean.labour_discount || 0) })}
        fields={[
          { key: "complaint", label: "Complaint", type: "textarea", required: true, max: 1000 },
          { key: "pay_type", label: "Who pays", type: "select", required: true, options: PAY },
          { key: "sold_hours", label: "Hours sold", type: "number", step: "any", min: 0 },
          { key: "labour_discount", label: "Labour discount", type: "number", step: "any", min: 0 },
          { key: "technician_employee_id", label: "Technician", type: "employee" },
          { key: "cause", label: "Cause", type: "textarea", max: 1000 },
          { key: "correction", label: "Correction", type: "textarea", max: 1000 },
        ]}
      />
      <FormDrawer
        show={adding}
        title="Add a job"
        initial={{ pay_type: "CUSTOMER" }}
        onHide={() => setAdding(false)}
        onDone={refresh}
        onSave={(clean) => dmsService.repairOrderAction(id, "jobs", { ...clean, sold_hours: clean.sold_hours === "" || clean.sold_hours == null ? undefined : Number(clean.sold_hours) })}
        fields={[
          { key: "complaint", label: "Complaint / work", type: "textarea", required: true, max: 1000 },
          { key: "pay_type", label: "Who pays", type: "select", required: true, options: PAY },
          { key: "labour_op_id", label: "Labour operation", type: "select", options: ops["labour-operations"] || [] },
          { key: "sold_hours", label: "Hours (blank: the operation's standard)", type: "number", step: "any", min: 0 },
        ]}
      />
      <FormDrawer
        show={gate}
        title="Gate pass"
        initial={{ odometer_out: ro ? ro.odometer_in : "" }}
        onHide={() => setGate(false)}
        onDone={refresh}
        onSave={(clean) => dmsService.repairOrderAction(id, "gate-pass", { odometer_out: clean.odometer_out === "" ? undefined : Number(clean.odometer_out) })}
        fields={[{ key: "odometer_out", label: "Odometer out (km)", type: "number", min: 0 }]}
      />
    </Panel>
  );
}

export default function RepairOrders() {
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const { data, loading, error, reload } = useLoad(() => dmsService.listRepairOrders({ status: status || undefined, open: status ? undefined : "1", q: q || undefined }), [status, q]);
  const [open, setOpen] = useState(null);
  const [creating, setCreating] = useState(false);
  return (
    <div style={pageStyle}>
      <PageHeader title="Repair Orders" subtitle="Workshop jobs from check-in to gate pass" actions={<PrimaryButton onClick={() => setCreating(true)}>Check in</PrimaryButton>} />
      <div className="d-flex flex-wrap gap-2 mb-3">
        <input className="form-control" style={{ ...inputStyle, width: 280 }} placeholder="RO, customer, VIN, plate, phone" aria-label="Search repair orders" value={q} onChange={(e) => setQ(e.target.value)} />
        <div style={{ width: 220 }}>
          <AppDropdown id="ro-st" value={status} options={[{ label: "Open orders", value: "" }, ...STATUSES.map((s) => ({ label: label(s), value: s }))]} onChange={(e) => setStatus(e.value)} />
        </div>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          onRowClick={(r) => setOpen(r.id)}
          empty={<EmptyState title="No repair orders" />}
          columns={[
            { label: "RO", render: (r) => <b>{r.ro_number}</b> },
            { label: "In", render: (r) => formatDateTime(r.checked_in_at) },
            { label: "Customer", render: (r) => r.customer_name },
            { label: "Vehicle", render: (r) => `${r.plate_no || ""} ${r.vehicle_desc || ""}`.trim() },
            { label: "Jobs", num: true, render: (r) => r.jobs },
            { label: "Promised", render: (r) => (r.promised_at ? formatDateTime(r.promised_at) : "") },
            { label: "Advisor", render: (r) => r.advisor_name },
            { label: "Estimate", num: true, render: (r) => fmtMoney(r.estimate_total) },
            { label: "Status", render: (r) => <DmsBadge status={r.status} /> },
          ]}
        />
      </LoadState>
      {open ? <RoView id={open} onHide={() => setOpen(null)} onChanged={reload} /> : null}
      {creating ? (
        <CheckIn
          onHide={() => setCreating(false)}
          onDone={(id) => {
            reload();
            setOpen(id);
          }}
        />
      ) : null}
    </div>
  );
}
